/**
 * Calculates the Levenshtein distance between two strings
 * This represents the minimum number of single-character edits needed to change one word into another
 * @param {string} str1 - First string to compare
 * @param {string} str2 - Second string to compare
 * @returns {number} The Levenshtein distance
 */
const levenshteinDistance = (str1, str2) => {
  if (!str1 || !str2) return Math.max(str1?.length || 0, str2?.length || 0);
  
  const s1 = str1?.toLowerCase();
  const s2 = str2?.toLowerCase();
  
  const matrix = [];
  
  // Initialize first column
  for (let i = 0; i <= s2?.length; i++) {
    matrix[i] = [i];
  }
  
  // Initialize first row
  for (let j = 0; j <= s1?.length; j++) {
    matrix[0][j] = j;
  }
  
  // Fill in the rest of the matrix
  for (let i = 1; i <= s2?.length; i++) {
    for (let j = 1; j <= s1?.length; j++) {
      if (s2?.charAt(i - 1) === s1?.charAt(j - 1)) {
        matrix[i][j] = matrix?.[i - 1]?.[j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix?.[i - 1]?.[j - 1] + 1, // substitution
          matrix?.[i]?.[j - 1] + 1,     // insertion
          matrix?.[i - 1]?.[j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix?.[s2?.length]?.[s1?.length];
};

/**
 * Calculates similarity score between two strings (0-1, where 1 is exact match)
 * @param {string} str1 - First string to compare
 * @param {string} str2 - Second string to compare
 * @returns {number} Similarity score between 0 and 1
 */
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1?.length, str2?.length);
  
  if (maxLength === 0) return 1;
  
  return 1 - (distance / maxLength);
};

/**
 * Checks if a string fuzzy matches a search term
 * @param {string} text - The text to search in
 * @param {string} searchTerm - The search term
 * @param {number} threshold - Similarity threshold (0-1), default 0.6
 * @returns {boolean} True if match is found within threshold
 */
export const fuzzyMatch = (text, searchTerm, threshold = 0.6) => {
  if (!text || !searchTerm) return false;
  
  const textLower = text?.toLowerCase();
  const searchLower = searchTerm?.toLowerCase();
  
  // Exact substring match - highest priority
  if (textLower?.includes(searchLower)) return true;
  
  // Split text into words for word-by-word comparison
  const words = textLower?.split(/\s+/);
  
  // Check each word against search term
  for (const word of words) {
    // Direct match on word
    if (word === searchLower) return true;
    
    // Fuzzy match on word
    const similarity = calculateSimilarity(word, searchLower);
    if (similarity >= threshold) return true;
    
    // Check if search term is contained within word with typos
    if (word?.length >= searchLower?.length) {
      for (let i = 0; i <= word?.length - searchLower?.length; i++) {
        const substring = word?.substring(i, i + searchLower?.length);
        const substringSimilarity = calculateSimilarity(substring, searchLower);
        if (substringSimilarity >= threshold) return true;
      }
    }
  }
  
  // Check full text similarity as fallback
  const fullSimilarity = calculateSimilarity(textLower, searchLower);
  return fullSimilarity >= threshold;
};

/**
 * Filters an array of objects based on fuzzy search across multiple fields
 * @param {Array} items - Array of objects to filter
 * @param {string} searchTerm - Search term
 * @param {Array<string>} searchFields - Fields to search in
 * @param {number} threshold - Similarity threshold (0-1), default 0.6
 * @returns {Array} Filtered array
 */
export const fuzzyFilter = (items, searchTerm, searchFields, threshold = 0.6) => {
  if (!searchTerm || !items?.length) return items;
  
  return items?.filter(item => {
    return searchFields?.some(field => {
      const value = item?.[field];
      if (!value) return false;
      
      const stringValue = String(value);
      return fuzzyMatch(stringValue, searchTerm, threshold);
    });
  });
};

/**
 * Highlights matching text in a string based on fuzzy search
 * @param {string} text - Original text
 * @param {string} searchTerm - Search term to highlight
 * @returns {string} Text with highlighted portions
 */
export const highlightFuzzyMatch = (text, searchTerm) => {
  if (!text || !searchTerm) return text;
  
  const textLower = text?.toLowerCase();
  const searchLower = searchTerm?.toLowerCase();
  
  // Find exact matches first
  const exactIndex = textLower?.indexOf(searchLower);
  if (exactIndex !== -1) {
    return (text?.substring(0, exactIndex) +
    '<mark>' + text?.substring(exactIndex, exactIndex + searchTerm?.length) + '</mark>' + text?.substring(exactIndex + searchTerm?.length));
  }
  
  return text;
};

export default {
  fuzzyMatch,
  fuzzyFilter,
  highlightFuzzyMatch,
  calculateSimilarity,
  levenshteinDistance
};